import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "../../icons/Icon";
import { Aviso, Carregando, ComOrdinais, Grupo, Item } from "../../components/Layout";
import {
  api, fmt,
  type Cargo, type CandidatoPerfil, type CandidatoQualificacaoDto, type Componente, type Edital,
  type ErroApi, type InscricaoResumo, type Regras,
} from "../../lib/api";

type Modalidade = "AMPLA" | "PCD" | "NEGRO";

/** Qual alínea do cargo escolhido um título do perfil vai preencher. */
type Mapeamento = { qualificacaoId: number; regraCodigo: string };

/** Sem nome de curso no perfil: identifica o título pela carga horária/data. */
function rotuloTitulo(t: CandidatoQualificacaoDto, indice: number): string {
  const partes = [`Título ${indice + 1}`];
  if (t.cargaHoraria) partes.push(`${t.cargaHoraria}h`);
  if (t.dataConclusao) partes.push(`concluído em ${fmt.data(t.dataConclusao)}`);
  return partes.join(" · ");
}

const PASSOS = ["Cargo e componente", "Modalidade", "Formação para este cargo", "Revisão"] as const;

/**
 * Mini-wizard de uma nova inscrição, de dentro da conta: dados pessoais,
 * experiência e formação já vêm do perfil — aqui só entra o que é
 * específico deste cargo (item 8.9: até 5 inscrições, 3+1+1 por grupo).
 */
export function NovaInscricao() {
  const [edital, setEdital] = useState<Edital | null>(null);
  const [perfil, setPerfil] = useState<CandidatoPerfil | null>(null);
  const [erroCarregamento, setErroCarregamento] = useState<string | null>(null);

  const [passo, setPasso] = useState(0);
  const [cargoId, setCargoId] = useState("");
  const [componenteId, setComponenteId] = useState("");
  const [modalidade, setModalidade] = useState<Modalidade>("AMPLA");
  const [regras, setRegras] = useState<Regras | null>(null);
  const [mapeamentoPorCategoria, setMapeamentoPorCategoria] = useState<
    Partial<Record<"I" | "II", Mapeamento>>
  >({});
  const [consentimento, setConsentimento] = useState(false);
  const [erros, setErros] = useState<string[]>([]);
  const [enviando, setEnviando] = useState(false);
  const [criada, setCriada] = useState<InscricaoResumo | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get<Edital>("/api/publico/edital"),
      api.get<CandidatoPerfil>("/api/candidato/perfil"),
    ]).then(([e, p]) => { setEdital(e); setPerfil(p); })
      .catch(() => setErroCarregamento("Não foi possível carregar os dados do edital e do seu perfil."));
  }, []);

  const cargo: Cargo | undefined = useMemo(
    () => edital?.cargos.find((c) => String(c.id) === cargoId),
    [edital, cargoId],
  );
  const componente: Componente | undefined = useMemo(
    () => cargo?.componentes.find((c) => String(c.id) === componenteId),
    [cargo, componenteId],
  );

  useEffect(() => {
    if (!cargo) { setRegras(null); return; }
    let ativo = true;
    api.get<Regras>(`/api/publico/cargos/${cargo.id}/regras`)
      .then((r) => { if (ativo) setRegras(r); })
      .catch(() => { if (ativo) setRegras(null); });
    return () => { ativo = false; };
  }, [cargo]);

  const calculo = useCalculo(perfil, regras, mapeamentoPorCategoria);

  function avancar() {
    const problemas = validar(passo, cargo, componente);
    setErros(problemas);
    if (problemas.length === 0) {
      setPasso((p) => Math.min(p + 1, PASSOS.length - 1));
      window.scrollTo({ top: 0 });
    }
  }

  function voltar() {
    setErros([]);
    setPasso((p) => Math.max(p - 1, 0));
    window.scrollTo({ top: 0 });
  }

  async function enviar() {
    if (!consentimento) {
      setErros(["É necessário aceitar o tratamento dos seus dados para concluir a inscrição."]);
      return;
    }
    setEnviando(true);
    setErros([]);
    try {
      const corpo = {
        cargoId: Number(cargoId),
        componenteId: componenteId ? Number(componenteId) : null,
        modalidade,
        mapeamentoQualificacoes: Object.values(mapeamentoPorCategoria).filter(Boolean),
        consentimentoLgpd: true,
        idempotencyKey: crypto.randomUUID(),
      };
      const r = await api.post<InscricaoResumo>("/api/candidato/inscricoes", corpo);
      setCriada(r);
      window.scrollTo({ top: 0 });
    } catch (e) {
      const err = e as ErroApi;
      setErros(err.motivos.length ? err.motivos : [err.mensagem]);
      window.scrollTo({ top: 0 });
    } finally {
      setEnviando(false);
    }
  }

  if (erroCarregamento) return <Aviso tipo="erro">{erroCarregamento}</Aviso>;
  if (!edital || !perfil) return <Carregando texto="Carregando" />;

  if (criada) {
    return (
      <div className="pilha pilha-6">
        <Aviso tipo="sucesso" titulo="Inscrição enviada">
          Sua inscrição para {criada.cargo} foi registrada
          {typeof criada.pontosTotal === "number"
            ? ` com ${fmt.pontos(criada.pontosTotal)} pontos declarados.`
            : "."} A pontuação será conferida na análise das inscrições.
        </Aviso>
        <div className="linha linha-quebra">
          <button type="button" className="tc-btn tc-btn-primario"
                  onClick={() => navigate(`/area/inscricoes/${criada.id}`)}>
            Ver inscrição e anexar comprovantes
          </button>
          <button type="button" className="tc-btn tc-btn-outline" onClick={() => navigate("/area")}>
            Voltar pra minha área
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="tc-layout-lateral">
      <div className="pilha pilha-6">
        <div className="pilha pilha-2">
          <p className="tc-ajuda">Etapa {passo + 1} de {PASSOS.length}</p>
          <h1 className="h2">{PASSOS[passo]}</h1>
        </div>

        {erros.length > 0 && (
          <Aviso tipo="erro">
            <ul>{erros.map((e, i) => <li key={i}>{e}</li>)}</ul>
          </Aviso>
        )}

        {passo === 0 && (
          <PassoCargo edital={edital} cargoId={cargoId} componenteId={componenteId} cargo={cargo}
                      aoEscolherCargo={(id) => { setCargoId(id); setComponenteId(""); setMapeamentoPorCategoria({}); }}
                      aoEscolherComponente={setComponenteId} />
        )}

        {passo === 1 && <PassoModalidade edital={edital} modalidade={modalidade} aoMudar={setModalidade} />}

        {passo === 2 && (
          <PassoFormacao perfil={perfil} regras={regras}
                         mapeamento={mapeamentoPorCategoria} aoMudar={setMapeamentoPorCategoria} />
        )}

        {passo === 3 && (
          <PassoRevisao cargo={cargo} componente={componente} modalidade={modalidade} perfil={perfil}
                        mapeamento={mapeamentoPorCategoria} regras={regras} edital={edital}
                        consentimento={consentimento} aoMudarConsentimento={setConsentimento} />
        )}

        <div className="linha linha-entre">
          {passo > 0 ? (
            <button type="button" className="tc-btn tc-btn-outline" onClick={voltar}>
              <Icon nome="esquerda" tamanho={18} />
              Voltar
            </button>
          ) : <span />}

          {passo < PASSOS.length - 1 ? (
            <button type="button" className="tc-btn tc-btn-primario" onClick={avancar}>
              Continuar
            </button>
          ) : (
            <button type="button" className="tc-btn tc-btn-primario" onClick={enviar} disabled={enviando}>
              {enviando ? "Enviando..." : "Enviar inscrição"}
            </button>
          )}
        </div>
      </div>

      <RecibdoPontuacao calculo={calculo} regras={regras} />
    </div>
  );
}

/** Pontuação declarada ao vivo — a "calculadora" do cadastro, agora aqui, onde o cargo já é conhecido. */
function RecibdoPontuacao({ calculo, regras }: { calculo: Calculo; regras: Regras | null }) {
  return (
    <aside className="tc-recibo" aria-label="Pontuação declarada até agora">
      <div>
        <p className="tc-recibo-titulo">Pontuação declarada</p>
        <p className="tc-recibo-sub">Atualiza enquanto você escolhe</p>
      </div>

      <dl className="tc-recibo-linhas">
        <div>
          <dt>
            Experiência
            <small>{calculo.mesesConsiderados} de {regras?.tetoMeses ?? 30} meses considerados</small>
          </dt>
          <dd className="num">{fmt.pontos(calculo.experiencia)}</dd>
        </div>
        <div>
          <dt>Formação</dt>
          <dd className="num">{fmt.pontos(calculo.qualificacao)}</dd>
        </div>
      </dl>

      <div className="tc-recibo-corte" aria-hidden="true" />

      <div className="tc-recibo-total">
        <span>Total</span>
        <span className="num">{fmt.pontos(calculo.total)}</span>
      </div>

      {regras && (
        <>
          <div className="tc-recibo-medidor" aria-hidden="true">
            <span style={{ width: `${Math.min(100, regras.maximoPossivel > 0 ? (calculo.total / regras.maximoPossivel) * 100 : 0)}%` }} />
          </div>
          <p className="tc-recibo-nota">
            Máximo possível para este cargo: {fmt.pontos(regras.maximoPossivel)} pontos.
          </p>
        </>
      )}

      {!regras && <p className="tc-recibo-nota">Escolha o cargo pra ver a pontuação.</p>}

      {calculo.avisos.map((a, i) => (
        <p key={i} className="tc-recibo-nota tc-recibo-aviso">{a}</p>
      ))}
    </aside>
  );
}

/* --------------------------------------------------------------- etapas --- */

function PassoCargo({ edital, cargoId, componenteId, cargo, aoEscolherCargo, aoEscolherComponente }: {
  edital: Edital; cargoId: string; componenteId: string; cargo: Cargo | undefined;
  aoEscolherCargo: (id: string) => void; aoEscolherComponente: (id: string) => void;
}) {
  return (
    <div className="pilha">
      <div className="tc-cartao">
        <fieldset className="pilha pilha-2">
          <legend className="tc-ajuda" style={{ marginBottom: 8 }}>
            Até 3 inscrições para Educador de Educação Básica, 1 para Educador Especialista
            Pedagógico e 1 para Monitor (item 8.9).
          </legend>
          {edital.cargos.map((c) => (
            <label key={c.id} className="opcao">
              <input type="radio" name="cargo" value={c.id}
                     checked={cargoId === String(c.id)}
                     onChange={() => aoEscolherCargo(String(c.id))} />
              <span>
                <span className="opcao-titulo">{c.codigo}: <ComOrdinais texto={c.nome} /></span>
                <span className="opcao-descricao"><ComOrdinais texto={c.campoAtuacao} /></span>
              </span>
            </label>
          ))}
        </fieldset>
      </div>

      {cargo && cargo.componentes.length > 0 && (
        <div className="tc-cartao">
          <fieldset className="pilha pilha-2">
            <legend className="tc-ajuda" style={{ marginBottom: 8 }}>
              {cargo.componentes.length > 1 ? "Componente curricular / função" : "Função"}
            </legend>
            {cargo.componentes.map((c) => (
              <label key={c.id} className="opcao">
                <input type="radio" name="componente" value={c.id}
                       checked={componenteId === String(c.id)}
                       onChange={() => aoEscolherComponente(String(c.id))} />
                <span>
                  <span className="opcao-titulo"><ComOrdinais texto={c.nome} /></span>
                  <span className="opcao-descricao"><ComOrdinais texto={c.preRequisito} /></span>
                </span>
              </label>
            ))}
          </fieldset>
        </div>
      )}
    </div>
  );
}

function PassoModalidade({ edital, modalidade, aoMudar }: {
  edital: Edital; modalidade: Modalidade; aoMudar: (m: Modalidade) => void;
}) {
  const pcd = edital.cotas.find((c) => c.tipo === "PCD");
  const negro = edital.cotas.find((c) => c.tipo === "NEGRO");
  return (
    <div className="tc-cartao">
      <fieldset className="pilha pilha-2">
        <label className="opcao">
          <input type="radio" name="modalidade" checked={modalidade === "AMPLA"}
                 onChange={() => aoMudar("AMPLA")} />
          <span>
            <span className="opcao-titulo">Ampla concorrência</span>
            <span className="opcao-descricao">Sem reserva de vagas.</span>
          </span>
        </label>
        <label className="opcao">
          <input type="radio" name="modalidade" checked={modalidade === "PCD"}
                 onChange={() => aoMudar("PCD")} />
          <span>
            <span className="opcao-titulo">Pessoa com Deficiência (PcD)</span>
            <span className="opcao-descricao">
              {pcd ? `${Number(pcd.percentual)}% das contratações.` : ""} Exige laudo médico na convocação.
            </span>
          </span>
        </label>
        <label className="opcao">
          <input type="radio" name="modalidade" checked={modalidade === "NEGRO"}
                 onChange={() => aoMudar("NEGRO")} />
          <span>
            <span className="opcao-titulo">Vaga reservada a negros</span>
            <span className="opcao-descricao">
              {negro ? `${Number(negro.percentual)}% das contratações.` : ""} Exige autodeclaração na convocação.
            </span>
          </span>
        </label>
      </fieldset>
    </div>
  );
}

function PassoFormacao({ perfil, regras, mapeamento, aoMudar }: {
  perfil: CandidatoPerfil; regras: Regras | null;
  mapeamento: Partial<Record<"I" | "II", Mapeamento>>;
  aoMudar: (m: Partial<Record<"I" | "II", Mapeamento>>) => void;
}) {
  const categorias: Array<"I" | "II"> = ["I", "II"];

  return (
    <div className="pilha">
      <Aviso tipo="info">
        A pontuação de cada título depende do cargo escolhido. Escolha qual título do seu
        perfil usar em cada categoria, e a alínea correspondente do Anexo II.
      </Aviso>

      {categorias.map((cat) => {
        const titulos = perfil.qualificacoes.filter((q) => q.categoria === cat);
        if (titulos.length === 0) return null;

        const alineas = regras?.qualificacao.filter((r) => r.categoria === cat) ?? [];
        const atual = mapeamento[cat];
        const tituloEscolhido = titulos.find((t) => t.id === atual?.qualificacaoId);

        return (
          <div key={cat} className="tc-cartao pilha">
            <strong>{cat === "I" ? "Formação acadêmica / titulação" : "Cursos de formação"}</strong>

            <div className="tc-campo">
              <label>Qual título usar nesta inscrição</label>
              <select value={atual?.qualificacaoId ?? ""} onChange={(e) => {
                const id = e.target.value ? Number(e.target.value) : null;
                const copia = { ...mapeamento };
                if (!id) { delete copia[cat]; } else { copia[cat] = { qualificacaoId: id, regraCodigo: "" }; }
                aoMudar(copia);
              }}>
                <option value="">Não usar título desta categoria</option>
                {titulos.map((t, idx) => (
                  <option key={t.id} value={t.id}>{rotuloTitulo(t, idx)}</option>
                ))}
              </select>
            </div>

            {tituloEscolhido && (
              <div className="tc-campo">
                <label>Alínea do Anexo II</label>
                <select value={atual?.regraCodigo ?? ""} onChange={(e) => {
                  aoMudar({ ...mapeamento, [cat]: { qualificacaoId: tituloEscolhido.id!, regraCodigo: e.target.value } });
                }}>
                  <option value="">Selecione a alínea</option>
                  {alineas.map((a) => (
                    <option key={a.codigo} value={a.codigo}>
                      {a.codigo}: {a.descricao} ({fmt.pontos(a.pontos)} pontos)
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        );
      })}

      {perfil.qualificacoes.length === 0 && (
        <p className="tc-ajuda">
          Você não tem título cadastrado no perfil. Pode seguir sem formação, ou
          adicionar títulos em "Meu perfil".
        </p>
      )}
    </div>
  );
}

function PassoRevisao({ cargo, componente, modalidade, perfil, mapeamento, regras, edital,
  consentimento, aoMudarConsentimento }: {
  cargo: Cargo | undefined; componente: Componente | undefined; modalidade: Modalidade;
  perfil: CandidatoPerfil; mapeamento: Partial<Record<"I" | "II", Mapeamento>>;
  regras: Regras | null; edital: Edital;
  consentimento: boolean; aoMudarConsentimento: (v: boolean) => void;
}) {
  const modalidades = { AMPLA: "Ampla concorrência", PCD: "Pessoa com Deficiência", NEGRO: "Vaga reservada a negros" };

  return (
    <div className="pilha">
      <div className="tc-cartao">
        <Grupo titulo="Cargo">
          <Item rotulo="Cargo">{cargo ? <>{cargo.codigo}: <ComOrdinais texto={cargo.nome} /></> : "—"}</Item>
          <Item rotulo="Componente / função">
            {componente ? <ComOrdinais texto={componente.nome} /> : "Não se aplica"}
          </Item>
          <Item rotulo="Modalidade">{modalidades[modalidade]}</Item>
        </Grupo>

        <Grupo titulo="Experiência (do seu perfil)">
          {perfil.experiencias.length === 0 && <Item rotulo="Períodos">Nenhum declarado</Item>}
          {perfil.experiencias.map((e, i) => (
            <Item key={i} rotulo={`Período ${i + 1}`}>
              {fmt.data(e.dataInicio)} a {e.dataFim ? fmt.data(e.dataFim) : "hoje (em andamento)"}
            </Item>
          ))}
        </Grupo>

        <Grupo titulo="Formação usada nesta inscrição">
          {Object.entries(mapeamento).length === 0 && <Item rotulo="Títulos">Nenhum</Item>}
          {(Object.entries(mapeamento) as Array<[string, Mapeamento | undefined]>).map(([cat, m]) => {
            if (!m) return null;
            const alinea = regras?.qualificacao.find((a) => a.categoria === cat && a.codigo === m.regraCodigo);
            return (
              <Item key={cat} rotulo={`Categoria ${cat}`}>
                Alínea {m.regraCodigo}{alinea ? `: ${alinea.descricao} (${fmt.pontos(alinea.pontos)} pontos)` : ""}
              </Item>
            );
          })}
        </Grupo>
      </div>

      <div className="tc-cartao">
        <label className="opcao">
          <input type="checkbox" checked={consentimento}
                 onChange={(e) => aoMudarConsentimento(e.target.checked)} />
          <span>
            <span className="opcao-titulo">Li e aceito o tratamento dos meus dados pessoais</span>
            <span className="opcao-descricao">{fmt.semRefs(edital.consentimentoTexto)}</span>
          </span>
        </label>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ validação --- */

function validar(passo: number, cargo: Cargo | undefined, componente: Componente | undefined): string[] {
  const erros: string[] = [];
  if (passo === 0) {
    if (!cargo) erros.push("Escolha o cargo pretendido.");
    else if (cargo.componentes.length > 0 && !componente) {
      erros.push(cargo.exigeComponente
        ? "Escolha o componente curricular para este cargo."
        : "Confirme a função deste cargo.");
    }
  }
  return erros;
}

/* -------------------------------------------------- cálculo transparente --- */

type Calculo = {
  experiencia: number; qualificacao: number; total: number;
  mesesConsiderados: number; avisos: string[];
};

/**
 * Espelha no cliente a mesma conta que o servidor faz (item 9.1.10), pra
 * mostrar a pontuação mudando em tempo real. O servidor continua sendo a
 * autoridade: este cálculo é informativo e nunca é enviado.
 */
function useCalculo(perfil: CandidatoPerfil | null, regras: Regras | null,
                    mapeamento: Partial<Record<"I" | "II", Mapeamento>>): Calculo {
  return useMemo(() => {
    const avisos: string[] = [];
    if (!perfil || !regras) {
      return { experiencia: 0, qualificacao: 0, total: 0, mesesConsiderados: 0, avisos };
    }

    const codigoDe = (vinculo: string) => (vinculo === "MUNICIPAL_BG" ? "A" : "B");

    // --- experiência: funde intervalos, desconta sobreposição, aplica o teto
    const porRegra = [...regras.experiencia].sort((a, b) => b.pontosPorMes - a.pontosPorMes);
    const consumido: Array<[number, number]> = [];
    let totalExp = 0;
    let mesesUsados = 0;
    let houveSobreposicao = false;

    for (const regra of porRegra) {
      const intervalos = perfil.experiencias
        .filter((e) => codigoDe(e.vinculo) === regra.codigo && e.dataInicio)
        // Sem data de fim = ainda em andamento: conta até hoje, igual o servidor faz.
        .map((e) => [Date.parse(e.dataInicio), Date.parse(e.dataFim || hojeISO()) + 86_400_000] as [number, number])
        .filter(([a, b]) => Number.isFinite(a) && Number.isFinite(b) && b > a);

      const fundidos = fundir(intervalos);
      const brutos = fundidos.reduce((s, [a, b]) => s + (b - a), 0);
      const liquidos = subtrair(fundidos, consumido);
      const dias = Math.round(liquidos.reduce((s, [a, b]) => s + (b - a), 0) / 86_400_000);
      if (liquidos.reduce((s, [a, b]) => s + (b - a), 0) < brutos) houveSobreposicao = true;

      consumido.push(...fundidos);

      const mesesApurados = Math.floor(dias / regras.diasPorMes);
      const disponivel = Math.max(0, regras.tetoMeses - mesesUsados);
      const mesesPontuados = Math.min(mesesApurados, disponivel);
      if (mesesPontuados < mesesApurados) {
        avisos.push(`Tempo limitado a ${regras.tetoMeses} meses pelo teto de pontuação.`);
      }
      mesesUsados += mesesPontuados;
      totalExp += mesesPontuados * regra.pontosPorMes;
    }
    if (houveSobreposicao) {
      avisos.push("Períodos sobrepostos foram contados uma única vez.");
    }

    // --- formação: soma a alínea escolhida em cada categoria (o código da
    // alínea só é único dentro da própria categoria — "A" existe nas duas)
    let totalQual = 0;
    for (const [cat, m] of Object.entries(mapeamento)) {
      if (!m) continue;
      const regra = regras.qualificacao.find((r) => r.categoria === cat && r.codigo === m.regraCodigo);
      if (regra) totalQual += regra.pontos;
    }

    const exp = Math.round(totalExp * 100) / 100;
    const qual = Math.round(totalQual * 100) / 100;
    return {
      experiencia: exp, qualificacao: qual,
      total: Math.round((exp + qual) * 100) / 100,
      mesesConsiderados: mesesUsados,
      avisos: [...new Set(avisos)],
    };
  }, [perfil, regras, mapeamento]);
}

/** Hoje em formato ISO (yyyy-mm-dd), pro mesmo Date.parse() usado nos períodos. */
function hojeISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function fundir(intervalos: Array<[number, number]>): Array<[number, number]> {
  if (!intervalos.length) return [];
  const ordenados = [...intervalos].sort((a, b) => a[0] - b[0]);
  const saida: Array<[number, number]> = [[...ordenados[0]] as [number, number]];
  for (let i = 1; i < ordenados.length; i++) {
    const atual = saida[saida.length - 1];
    const prox = ordenados[i];
    if (prox[0] <= atual[1]) atual[1] = Math.max(atual[1], prox[1]);
    else saida.push([...prox] as [number, number]);
  }
  return saida;
}

function subtrair(base: Array<[number, number]>, remover: Array<[number, number]>): Array<[number, number]> {
  if (!remover.length) return base;
  const corte = fundir(remover);
  const saida: Array<[number, number]> = [];
  for (const [inicioBase, fimBase] of base) {
    let inicio = inicioBase;
    for (const [a, b] of corte) {
      if (b <= inicio) continue;
      if (a >= fimBase) break;
      if (a > inicio) saida.push([inicio, Math.min(a, fimBase)]);
      inicio = Math.max(inicio, b);
      if (inicio >= fimBase) break;
    }
    if (inicio < fimBase) saida.push([inicio, fimBase]);
  }
  return saida;
}
